/** Draft state + wire conversion for the batch-edit dialog.
 *
 *  The dialog edits DRAFTS (stable row ids for React keys, raw editor values);
 *  `buildBlocks` converts them into the wire `BatchEditBlock[]` shape. The
 *  conditions UI is deliberately flat — one ALL/ANY combinator plus a per-row
 *  NOT — but the wire format supports nested groups, so `ANY` simply wraps the
 *  leaves in a single OR group (the API needs no change if a nested builder
 *  arrives later).
 */
import type {
  BatchEditBlock,
  BatchEditCondition,
  BatchEditConditionGroup,
  BatchEditPath,
} from '@/api/batchEdit';
import { PRESENCE_OPERATORS } from '@/api/batchEdit';

export interface ConditionDraft {
  id: string;
  /** Dotty path into the row's read document (free-form — conditions may read
   *  any documented field, not just editable ones). */
  path: string;
  op: string;
  /** Raw editor value; converted per the referenced entry's type (when the
   *  path is a catalog entry) at build time. */
  value: unknown;
  not: boolean;
}

export interface RegexValueDraft {
  pattern: string;
  replacement: string;
  ignoreCase: boolean;
}

export interface OpDraft {
  id: string;
  /** Canonical editable path (picked from the catalog). */
  path: string;
  op: string;
  value: unknown;
  regex: RegexValueDraft;
}

export interface BlockDraft {
  id: string;
  combinator: 'AND' | 'OR';
  conditions: ConditionDraft[];
  ops: OpDraft[];
}

let seq = 0;
const nextId = () => `bd-${++seq}`;

export function emptyCondition(): ConditionDraft {
  return { id: nextId(), path: '', op: 'eq', value: '', not: false };
}

export function emptyOp(): OpDraft {
  return {
    id: nextId(),
    path: '',
    op: 'set',
    value: '',
    regex: { pattern: '', replacement: '', ignoreCase: false },
  };
}

export function emptyBlock(): BlockDraft {
  return { id: nextId(), combinator: 'AND', conditions: [], ops: [emptyOp()] };
}

/** Ops that take no value at all. */
export const VALUELESS_OPS = new Set(['flip', 'unset']);
/** Ops whose value is a duration string like `1y2M1w3d7h1m33s`. */
export const DURATION_OPS = new Set(['add', 'subtract']);
/** Ops whose value is always numeric regardless of entry type. */
export const NUMERIC_OPS = new Set(['inc', 'dec']);
/** Ops whose value is always a plain string regardless of entry type. */
export const STRING_OPS = new Set(['append', 'prepend']);

export function entryByPath(paths: BatchEditPath[] | undefined, path: string): BatchEditPath | null {
  return paths?.find((p) => p.path === path) ?? null;
}

const DELTA_RE = /^(\d+[yMwdhms])+$/;

// ---------------------------------------------------------------------------
// Value conversion
// ---------------------------------------------------------------------------

/** Convert a raw editor value to the wire value for a mutation op. Returns
 *  `{ ok: false }` when the raw value cannot make a valid wire value. */
export function opWireValue(
  entry: BatchEditPath | null,
  draft: OpDraft,
): { ok: boolean; value?: unknown } {
  const op = draft.op;
  if (VALUELESS_OPS.has(op)) return { ok: true };
  if (op === 'regex_replace') {
    if (!draft.regex.pattern) return { ok: false };
    return {
      ok: true,
      value: {
        pattern: draft.regex.pattern,
        replacement: draft.regex.replacement,
        ignore_case: draft.regex.ignoreCase,
      },
    };
  }
  if (DURATION_OPS.has(op)) {
    const text = String(draft.value ?? '').trim();
    return DELTA_RE.test(text) ? { ok: true, value: text } : { ok: false };
  }
  if (NUMERIC_OPS.has(op)) {
    return coerceNumber(draft.value, entry?.type === 'integer');
  }
  if (STRING_OPS.has(op)) {
    const text = String(draft.value ?? '');
    return text.length > 0 ? { ok: true, value: text } : { ok: false };
  }
  // set / set_if_null — typed by the entry.
  return typedWireValue(entry, draft.value);
}

/** Convert a raw editor value per the entry type (used by set/set_if_null and
 *  by condition right-hand constants when the left path is a known entry). */
export function typedWireValue(
  entry: BatchEditPath | null,
  raw: unknown,
): { ok: boolean; value?: unknown } {
  const type = entry?.type ?? 'string';
  if (type === 'enum') {
    // Enum editors emit the real choice value; '' means "not picked yet".
    return raw === '' || raw === undefined ? { ok: false } : { ok: true, value: raw };
  }
  if (type === 'boolean') {
    if (typeof raw === 'boolean') return { ok: true, value: raw };
    if (raw === 'true') return { ok: true, value: true };
    if (raw === 'false') return { ok: true, value: false };
    return { ok: false };
  }
  if (type === 'integer' || type === 'number') {
    return coerceNumber(raw, type === 'integer');
  }
  // string / datetime / date / time travel as (non-empty) strings.
  const text = String(raw ?? '');
  return text.length > 0 ? { ok: true, value: text } : { ok: false };
}

function coerceNumber(raw: unknown, integer: boolean): { ok: boolean; value?: unknown } {
  const text = String(raw ?? '').trim();
  if (text === '') return { ok: false };
  const num = Number(text);
  if (!Number.isFinite(num)) return { ok: false };
  if (integer && !Number.isInteger(num)) return { ok: false };
  return { ok: true, value: num };
}

/** The coerce hint for a condition, derived from the left path's entry type. */
function coerceHint(entry: BatchEditPath | null): BatchEditCondition['coerce'] {
  if (!entry) return undefined;
  if (entry.type === 'integer' || entry.type === 'number' || entry.type === 'enum') return 'number';
  if (entry.type === 'boolean') return 'boolean';
  if (entry.type === 'datetime' || entry.type === 'date') return 'datetime';
  return undefined;
}

// ---------------------------------------------------------------------------
// Validation + wire build
// ---------------------------------------------------------------------------

export function conditionComplete(paths: BatchEditPath[] | undefined, c: ConditionDraft): boolean {
  if (!c.path.trim() || !c.op) return false;
  if ((PRESENCE_OPERATORS as readonly string[]).includes(c.op)) return true;
  const entry = entryByPath(paths, c.path.trim());
  return typedWireValue(entry, c.value).ok;
}

export function opComplete(paths: BatchEditPath[] | undefined, o: OpDraft): boolean {
  const entry = entryByPath(paths, o.path);
  if (!entry || !entry.ops.includes(o.op)) return false;
  return opWireValue(entry, o).ok;
}

export function blockComplete(paths: BatchEditPath[] | undefined, b: BlockDraft): boolean {
  if (b.ops.length === 0) return false;
  return b.ops.every((o) => opComplete(paths, o)) && b.conditions.every((c) => conditionComplete(paths, c));
}

export function buildBlocks(paths: BatchEditPath[] | undefined, blocks: BlockDraft[]): BatchEditBlock[] {
  return blocks.map((b) => {
    const leaves: BatchEditCondition[] = b.conditions.map((c) => {
      const path = c.path.trim();
      const entry = entryByPath(paths, path);
      const leaf: BatchEditCondition = {
        left: { type: 'dotty', value: path },
        op: c.op,
      };
      if (!(PRESENCE_OPERATORS as readonly string[]).includes(c.op)) {
        leaf.right = { type: 'constant', value: typedWireValue(entry, c.value).value };
        const coerce = coerceHint(entry);
        if (coerce) leaf.coerce = coerce;
      }
      if (c.not) leaf.not = true;
      return leaf;
    });
    const conditions: Array<BatchEditCondition | BatchEditConditionGroup> | undefined =
      leaves.length === 0
        ? undefined
        : b.combinator === 'OR' && leaves.length > 1
          ? [{ bool: 'OR', conditions: leaves }]
          : leaves;
    return {
      ...(conditions ? { conditions } : {}),
      ops: b.ops.map((o) => {
        const entry = entryByPath(paths, o.path);
        const wire = opWireValue(entry, o);
        return {
          path: o.path,
          op: o.op,
          ...(wire.value !== undefined ? { value: wire.value } : {}),
        };
      }),
    };
  });
}
